import React, { useState, PropsWithChildren, useMemo } from "react";

import {
  Button,
  Checkbox,
  CircularProgress,
  Grid,
  Paper,
  TextField,
  Typography,
} from "@material-ui/core";
import { Color, ColorPicker, createColor } from "material-ui-color";

import { MUTATIONS } from "../../../../graphql";
import {
  BucketAttachment,
  Building,
  BuildingGuestParking,
  BuildingLaundryOpts,
} from "../../../../graphql/models";

import useBuildingFormStyle from "./building_form.style";
import { useLoggedUserInfo, useSnackbarMutation } from "../../../../hooks";
import {
  AttachmentsPicker,
  BuildingFaqMediatorIn,
  BuildingFaqMediatorOut,
  BuildingFaqsEditor,
  BuildingLaundryOptsEditor,
  BuildingMediaEditor,
  BuildingMediaRoomEditor,
  BuildingOnboardingComponent,
  ImagePicker,
  PhoneInput,
  pBuildingFaq,
} from "../../../components";
import { BuildingGuestParkingComponent } from "../../../components/building_guest_parking/building_guest_parking";
import { PaymentConfigs } from "../../../components/payments";

export interface BuildingEditFormProps extends PropsWithChildren<any> {
  value: Building | undefined;
  onValueUpdatedSuccessfully: (v: Building) => void;
}

export function BuildingForm(props: BuildingEditFormProps) {
  const { value, onValueUpdatedSuccessfully } = props;
  const classes = useBuildingFormStyle();

  const [primaryColor, setPrimaryColor] = useState<Color>(
    value?.primaryColor
      ? createColor(value?.primaryColor)
      : createColor("#e9e9e9")
  );
  const [logo, setLogo] = useState<string | undefined>(value?.logo);

  const [
    invokeCreateOrUpdateSystemBuilding,
    { loading, data: createOrUpdateBuildingData },
  ] = useSnackbarMutation(
    value?.id ? MUTATIONS.UPDATE_BUILDING : MUTATIONS.CREATE_BUILDING
  );
  const [updated, setUpdated] = useState(false);

  const [name, setName] = useState<string>(value?.name ?? "");
  const [code, setCode] = useState<string | undefined>(value?.code);
  const [mainPhone, setMainPhone] = useState<string | undefined>(
    value?.mainPhone
  );
  const [rulesDoc, setRulesDoc] = useState<BucketAttachment | undefined>(
    value?.rulesDoc ?? void 0
  );
  const [faqs, setFaqs] = useState<pBuildingFaq[]>(
    BuildingFaqMediatorIn(value?.faqs)
  );
  const [laundryOpts, setLaundryOpts] = useState<BuildingLaundryOpts>(
    value?.laundryOpts ?? {}
  );
  const [disableDomotics, setDisableDomotics] = useState<boolean>(
    value?.disableDomotics ?? false
  );
  const [hasElevator, setHasElevator] = useState(value?.hasElevator ?? false);
  const [showInstalments, setShowInstalments] = useState(
    value?.showInstalments ?? false
  );
  const [enableInstalments, setEnableInstalments] = useState(
    value?.enableInstalments ?? false
  );
  const [otherAttachments, setOtherAttachments] = useState<BucketAttachment[]>(
    value?.otherAttachments ?? []
  );
  const [airKeyApiKey, setAirKeyApiKey] = useState<string>(
    value?.airKeyApiKey ?? ""
  );
  const [latitude, setLatitude] = useState(value?.latitude?.toString() ?? "");
  const [longitude, setLongitude] = useState(
    value?.longitude?.toString() ?? ""
  );
  const [zipCode, setZipCode] = useState(value?.zipCode ?? "");
  const [onboarding, setOnboarding] = useState(
    value?.onboarding ?? { i18n: {} }
  );
  const guestParkingDefault: BuildingGuestParking = useMemo(() => {
    return {
      parkingCodes: [],
      devices: [],
    };
  }, []);
  const [guestParking, setGuestParking] = useState(
    value?.guestParking ?? { ...guestParkingDefault }
  );
  const [paymentConfigs, setPaymentConfigs] = useState(
    value?.instalmentsPaymentConfigs
  );

  const { isAdminUser } = useLoggedUserInfo();

  const onFormSubmit = (evt) => {
    evt.preventDefault();
    invokeCreateOrUpdateSystemBuilding({
      variables: {
        buildingId: value?.id,
        name,
        code,
        primaryColor: primaryColor ? `#${primaryColor.hex}` : void 0,
        logo,
        mainPhone,
        latitude: parseFloat(latitude),
        longitude: parseFloat(longitude),
        zipCode,
        hasElevator,
        rulesDoc,
        faqs: BuildingFaqMediatorOut(faqs),
        laundryOpts,
        otherAttachments,
        disableDomotics,
        airKeyApiKey,
        onboarding,
        guestParking,
        showInstalments,
        enableInstalments,
        instalmentsPaymentConfigs: paymentConfigs,
      },
    });
  };

  if (createOrUpdateBuildingData && !updated) {
    onValueUpdatedSuccessfully(createOrUpdateBuildingData.createSystemBuilding);
    setUpdated(true);
  }

  return (
    <>
      {loading && `Loading...`}
      {!createOrUpdateBuildingData && (
        <form
          className={classes.root}
          onSubmit={onFormSubmit}
          noValidate
          autoComplete="off"
        >
          <Grid container spacing={3}>
            <Grid item sm={4} xs={12}>
              <TextField
                required
                className={classes.textField}
                label="Name"
                variant="outlined"
                value={name}
                onChange={(e) => setName(e.target.value)}
              />
            </Grid>
            <Grid item sm={4} xs={12}>
              <TextField
                className={classes.textField}
                label="Codice"
                variant="outlined"
                value={code}
                onChange={(e) => setCode(e.target.value)}
              />
            </Grid>
            <Grid item sm={4} xs={12} />
            <Grid item sm={3} xs={12}>
              <div style={{ display: "flex", alignItems: "center" }}>
                <Checkbox
                  checked={disableDomotics}
                  onChange={(_, v) => setDisableDomotics(v)}
                />
                <Typography variant="h6">
                  Disabilita la domotica in-app?
                </Typography>
              </div>
            </Grid>
            <Grid item sm={3} xs={12}>
              <div style={{ display: "flex", alignItems: "center" }}>
                <Checkbox
                  checked={hasElevator}
                  onChange={(_, v) => setHasElevator(v)}
                />
                <Typography variant="h6">Il palazzo ha l'ascensore?</Typography>
              </div>
            </Grid>
            <Grid item sm={3} xs={12}>
              <div style={{ display: "flex", alignItems: "center" }}>
                <Checkbox
                  checked={showInstalments}
                  onChange={(_, v) => setShowInstalments(v)}
                />
                <Typography variant="h6">Mostra Rate Condominiali</Typography>
              </div>
            </Grid>
            <Grid item sm={3} xs={12}>
              <div style={{ display: "flex", alignItems: "center" }}>
                <Checkbox
                  checked={enableInstalments}
                  onChange={(_, v) => setEnableInstalments(v)}
                />
                <Typography variant="h6">Abilita Rate Condominiali</Typography>
              </div>
            </Grid>
            <Grid item md={3} sm={4} xs={12}>
              <PhoneInput
                className={classes.textField}
                label="Numero di telefono Concierge"
                variant="outlined"
                defaultValue={mainPhone}
                onChange={(e) => setMainPhone(e.target.value)}
              />
            </Grid>
            {isAdminUser && (
              <Grid item sm={3} xs={12}>
                <TextField
                  className={classes.textField}
                  label="API-Key di AirKey"
                  variant="outlined"
                  value={airKeyApiKey}
                  onChange={(e) => setAirKeyApiKey(e.target.value.trim())}
                ></TextField>
              </Grid>
            )}
            <Grid item sm={4} xs={12}>
              <ColorPicker value={primaryColor} onChange={setPrimaryColor} />
            </Grid>
            <Grid item sm={3} xs={12}>
              <TextField
                className={classes.textField}
                label="Latitudine"
                variant="outlined"
                value={latitude}
                onChange={(e) => setLatitude(e.target.value.trim())}
              />
            </Grid>
            <Grid item sm={3} xs={12}>
              <TextField
                className={classes.textField}
                label="Longitudine"
                variant="outlined"
                value={longitude}
                onChange={(e) => setLongitude(e.target.value.trim())}
              />
            </Grid>
            <Grid item sm={3} xs={12}>
              <TextField
                className={classes.textField}
                label="Codice Postale"
                variant="outlined"
                value={zipCode}
                onChange={(e) => setZipCode(e.target.value.trim())}
              />
            </Grid>
            <Grid item sm={12} xs={12}>
              <Typography variant="h3">
                PDF regolamento di condominio
              </Typography>
              <AttachmentsPicker
                defaultValue={rulesDoc ? [rulesDoc] : void 0}
                onUpdated={(v) => setRulesDoc(v[0])}
              />
            </Grid>
            <Grid item sm={12} xs={12}>
              <Typography variant="h3">Altri allegati</Typography>
              <AttachmentsPicker
                defaultValue={otherAttachments}
                onUpdated={(v) => setOtherAttachments(v)}
              />
            </Grid>
            <Grid item xs={12}>
              <PaymentConfigs
                title="Configurazione per pagamenti rate condominiali"
                value={paymentConfigs}
                onChange={setPaymentConfigs}
              ></PaymentConfigs>
            </Grid>
            <Grid item sm={12} xs={12}>
              <Paper elevation={3} className={classes.paper}>
                <Typography variant="h6">FAQ</Typography>
                <br />
                <BuildingFaqsEditor faqs={faqs} onChange={(v) => setFaqs(v)} />
              </Paper>
            </Grid>
            <Grid item sm={12} xs={12}>
              <Paper elevation={3} className={classes.paper}>
                <Typography variant="h6">LOGO</Typography>
                <ImagePicker
                  height={300}
                  width={300}
                  defaultValue={logo}
                  onUploaded={(e) => setLogo(e.url)}
                />
              </Paper>
            </Grid>
            {value?.id && value?.code && !disableDomotics && (
              <Grid item sm={12} xs={12}>
                <BuildingMediaRoomEditor
                  buildingId={value?.id}
                  buildingCode={value?.code}
                />
              </Grid>
            )}
            {value?.id && value?.code && !disableDomotics && (
              <Grid item sm={12} xs={12}>
                <BuildingMediaEditor
                  buildingId={value?.id}
                  buildingCode={value?.code}
                />
              </Grid>
            )}
            <Grid item sm={12} xs={12}>
              <BuildingLaundryOptsEditor
                laundryOpts={laundryOpts}
                onLaundryOptsChange={setLaundryOpts}
              />
            </Grid>
            {false && (
              <Grid item xs={12}>
                <BuildingOnboardingComponent
                  onboarding={onboarding}
                  onChange={setOnboarding}
                ></BuildingOnboardingComponent>
              </Grid>
            )}
            <Grid item xs={12}>
              <BuildingGuestParkingComponent
                value={guestParking}
                onChange={setGuestParking}
              ></BuildingGuestParkingComponent>
            </Grid>
            <Grid item sm={12} xs={12}>
              <Button
                className={classes.textField}
                type="submit"
                variant="contained"
                color="primary"
              >
                {!loading ? "Salva" : <CircularProgress />}
              </Button>
            </Grid>
          </Grid>
        </form>
      )}
    </>
  );
}
